import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { ConfirmModal } from '@app/components/Modals';
import { ConfirmCtx } from '@app/providers/NewConfirmProvider/NewConfirmContext';
import type {
  ConfirmContext,
  ConfirmOptions,
} from '@app/providers/NewConfirmProvider/NewConfirmProvider.types';

interface Props {
  children: ReactNode;
}

export function NewConfirmProvider({ children }: Props) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const closeModal = () => {
    setOpen(false);
    setOptions(null);
  };

  const show = useCallback((confirmOptions: ConfirmOptions) => {
    setOptions(confirmOptions);
    setOpen(true);
  }, []);

  const contextValue: ConfirmContext = useMemo(
    () => ({ show, open }),
    [show, open]
  );

  const handleConfirm = () => {
    if (options?.onConfirm) {
      options.onConfirm();
    }
    closeModal();
  };

  const handleCancel = () => {
    if (options?.onCancel) {
      options.onCancel();
    }
    closeModal();
  };

  const handleDiscard = () => {
    if (options?.onDiscard) {
      options.onDiscard();
    }
    closeModal();
  };

  return (
    <ConfirmCtx.Provider value={contextValue}>
      {children}

      <ConfirmModal
        {...options}
        open={open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onDiscard={handleDiscard}
      />
    </ConfirmCtx.Provider>
  );
}
